/**
 * Auth FLOWS: sign-in, session watching, and the two-factor story
 * (standing policy: 2FA always -- TOTP enrollment gate + sign-in
 * challenge). The firebase INSTANCE and authFetch live in api/; this
 * module owns everything a human does with them.
 */
import {
  GoogleAuthProvider,
  TotpMultiFactorGenerator,
  type TotpSecret,
  createUserWithEmailAndPassword,
  getMultiFactorResolver,
  getRedirectResult,
  multiFactor,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut,
  type MultiFactorError,
  type MultiFactorResolver,
  type User,
} from 'firebase/auth';
import { AUTH_EMULATOR_URL, auth } from '../../api';

export type { User };

export function watchUser(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

/**
 * REDIRECT flow, not popup: the auth handler is same-origin
 * (authDomain = the site's own domain), so full-page redirect is the
 * partitioning-proof, popup-blocker-proof path Google recommends.
 * Completion lands via onAuthStateChanged after the return trip;
 * completeRedirectSignIn surfaces any error the trip carried.
 */
export function signInWithGoogle(email: string): Promise<never> {
  const provider = new GoogleAuthProvider();
  const domain = email.slice(email.lastIndexOf('@') + 1).toLowerCase();

  // TWO PARAMETERS, AND ONLY ONE OF THEM IS A REQUEST.
  //
  // login_hint names the account, so Google signs THIS person in rather
  // than asking which person. They answered that at the front door. It
  // is a convenience and nothing more -- a hint can be ignored.
  //
  // hd is ENFORCED BY GOOGLE. It refuses to complete a sign-in for any
  // account outside that hosted domain, which is what makes it the
  // strongest link in this chain: an org hosts its own email domain, so
  // a stranger cannot authenticate into a customer's tenant AT ALL --
  // not refused afterwards, not signed out and bounced, but never
  // issued a token. Nothing is written into the user pool, because the
  // sign-in does not happen.
  //
  // It is not the whole answer. hd only binds Workspace domains, and
  // Google's own guidance is to verify the returned claim rather than
  // trust the request -- which core/platform/auth.py does on every call
  // by deriving the org from the verified address. This stops the
  // attempt; that refuses the result.
  provider.setCustomParameters({ login_hint: email, hd: domain });
  return signInWithRedirect(auth, provider);
}

export async function completeRedirectSignIn(): Promise<unknown> {
  try {
    await getRedirectResult(auth);
    return null;
  } catch (err) {
    return err;
  }
}

export function signInWithEmail(email: string, password: string): Promise<unknown> {
  return signInWithEmailAndPassword(auth, email, password);
}

export function registerWithEmail(email: string, password: string): Promise<unknown> {
  return createUserWithEmailAndPassword(auth, email, password);
}

export function signOutUser(): Promise<void> {
  return signOut(auth);
}

/**
 * Two-factor, always (standing policy): TOTP is the second factor.
 * Sign-in of an enrolled user throws auth/multi-factor-auth-required;
 * the resolver + a 6-digit code completes it. Un-enrolled users are
 * gated into enrollment by the shell (TotpEnroll) before anything else
 * -- and once every user is enrolled, the server flips to MANDATORY.
 */
export type MfaChallenge = { resolver: MultiFactorResolver };

export function mfaChallengeFromError(err: unknown): MfaChallenge | null {
  const code = (err as { code?: string } | null)?.code;
  if (code !== 'auth/multi-factor-auth-required') return null;
  return { resolver: getMultiFactorResolver(auth, err as MultiFactorError) };
}

export function completeMfaSignIn(challenge: MfaChallenge, code: string): Promise<unknown> {
  const hint =
    challenge.resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID) ??
    challenge.resolver.hints[0];
  return challenge.resolver.resolveSignIn(
    TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code),
  );
}

/** Emulator dev skips enforcement (fake project, no real factors). */
export function needsTotpEnrollment(user: User): boolean {
  if (AUTH_EMULATOR_URL) return false;
  return multiFactor(user).enrolledFactors.length === 0;
}

export async function startTotpEnrollment(
  user: User,
): Promise<{ secret: TotpSecret; otpauthUri: string }> {
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  return {
    secret,
    otpauthUri: secret.generateQrCodeUrl(user.email ?? 'polywog-user', 'Polywog'),
  };
}

export async function finishTotpEnrollment(
  user: User,
  secret: TotpSecret,
  code: string,
): Promise<void> {
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
  await multiFactor(user).enroll(assertion, 'Authenticator');
}
